/**
 * CaseQueue.jsx
 * Operational Queue Control and Case Validation Node Component Implementation
 */

import React, { useState, useEffect, useCallback } from 'react';
import './CaseQueue.css';
import { api, downloadFile } from '../../api/rapid-imlementation-workflow-api';
import {
  currentRole, isTabAllowed, CHROME_TABS, canCreateIntake, canResolveClaim,
  canViewClaimReadOnly, canDownloadChecklists, canOpenIntakeCase,
  canValidateInstall, canOpenChecklistTab, queueStatusFilterForRole, roleStatusLabel
} from './roleAccess';

export default function CaseQueue({ onTabChange }) {
  const role = currentRole();
  // Queue Framework Telemetry States
  const [quotes, setQuotes] = useState([]);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('Loading cases...');
  const [includeInstallComplete, setIncludeInstallComplete] = useState(false);

  // Sub-Modal Workspace States
  const [validationContext, setValidationContext] = useState(null);
  const [inaccuracyContext, setInaccuracyContext] = useState(null);

  // Main Data Loading Pipeline
  const loadQueueData = useCallback(async () => {
    setLoading(true);
    //setStatusMessage("Refreshing...");
    try {
      const data = await api.fetchQueueRecords(includeInstallComplete);
      const records = (data.quotes || []).filter(q => queueStatusFilterForRole(role, q));
      setQuotes(records);
      setDuplicateGroups(data.duplicateGroups || []);
      setStatusMessage("Ready to Install means AE approval is complete. Use Open case to validate installed sites.");
      //records.length ? 
      //  `${records.length} active implementation profile${records.length === 1 ? '' : 's'} mapped.` : 
       
   
    } catch (e) {
      setStatusMessage(`Error occurred when fetching records: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [includeInstallComplete, role]);

  useEffect(() => {
    const initializePortal = async () => {
        try {
        await api.logPortalAccess('Case Queue', '/caseQueue');
        } catch (err) {
        console.warn('[Telemetry Core] Failed to register portal access event:', err.message);
        }
    };
        initializePortal().then(() => {
    });
    loadQueueData();
  }, [loadQueueData]);

  // Operational Validation Mapping Logic
  const initiateSiteValidation = async (id) => {
    setInaccuracyContext(null);
    setValidationContext({ loading: true, saving: false, message: "Querying active intake validation properties...", intake: null, installedSiteIds: [], installedBy: "", notes: "" });
    try {
      const data = await api.fetchValidationDetails(id);
      const intake = data.intake || {};
      const install = intake.installation || {};
      setValidationContext({
        loading: false, saving: false,
        message: "Isolate and mark the precise structural facility elements deployed, then commit progress changes.",
        intake,
        installedSiteIds: install.installedSiteIds || [],
        installedBy: install.installedBy || "",
        notes: install.notes || ""
      });
    } catch (e) {
      setValidationContext(prev => ({ ...prev, loading: false, message: `Failed retrieving context allocation metadata: ${e.message}` }));
    }
  };

  const handleValidationCheckboxChange = (siteId, checked) => {
    if (!validationContext) return;
    const currentSet = new Set(validationContext.installedSiteIds);
    if (checked) currentSet.add(siteId);
    else currentSet.delete(siteId);
    setValidationContext(prev => ({ ...prev, installedSiteIds: Array.from(currentSet) }));
  };

  const commitValidationPayload = async () => {
    const v = validationContext;
    if (!v?.intake) return;
    if (!v.installedSiteIds.length) {
      setValidationContext(prev => ({ ...prev, message: "An installation must map to at least one valid site element before deployment phase log lock." }));
      return;
    }
    setValidationContext(prev => ({ ...prev, saving: true, message: "Committing validation boundaries to data clusters..." }));
    try {
      const data = await api.saveValidationPayload(v.intake.id, {
        installedSiteIds: v.installedSiteIds,
        installedBy: v.installedBy,
        notes: v.notes
      });
      const intake = data.intake || v.intake;
      setValidationContext(prev => ({
        ...prev, saving: false, intake, message: `Structural footprint updated successfully. State transition: ${intake.statusLabel || 'Updated'}`
      }));
      await loadQueueData();
    } catch (e) {
      setValidationContext(prev => ({ ...prev, saving: false, message: `Submission write error aborted: ${e.message}` }));
    }
  };

  // Inaccuracy Resolution Mechanics Pipeline
  const initiateInaccuracyReview = async (id) => {
    setValidationContext(null);
    setInaccuracyContext({ loading: true, saving: false, message: "Retrieving metadata tracking discrepancies payload...", intake: null, reason: "" });
    try {
      const intake = await api.getIntake(id);
      setInaccuracyContext({
        loading: false, saving: false,
        message: "Review the AE comment, then approve the request or mark that a new quote is needed.",
        intake, reason: ""
      });
    } catch (e) {
      setInaccuracyContext(prev => ({ ...prev, loading: false, message: `Failed populating exception log trace: ${e.message}` }));
    }
  };

  const commitInaccuracyResolution = async (decision) => {
    const rx = inaccuracyContext;
    if (!rx?.intake) return;
    const normalizedReason = (rx.reason || "").trim();
    if (decision === "new_quote_needed" && !normalizedReason) {
      setInaccuracyContext(prev => ({ ...prev, message: "Enter the reason a new quote is needed before saving." }));
      return;
    }
    setInaccuracyContext(prev => ({ ...prev, saving: true, message: decision === "approve" ? "Approving request and opening IM revision..." : "Marking New Quote Needed..." }));
    try {
      const data = await api.submitInaccuracyResolution(rx.intake.id, decision, normalizedReason);
      setInaccuracyContext(prev => ({ ...prev, saving: false, message: `Resolution recorded. Case is now ${data.statusLabel || "updated"}.` }));
      await loadQueueData();
    } catch (e) {
      setInaccuracyContext(prev => ({ ...prev, saving: false, message: `Save failed: ${e.message}` }));
    }
  };

  // Helper Formatters
  const formatEpochTimestamp = (val) => {
    if (!val) return "-";
    const parsedDate = new Date(val);
    if (Number.isNaN(parsedDate.getTime())) return val;
    return parsedDate.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  };

  // Hand-off from the Queue straight into the Service Install Checklist with the
  // quote + intake already selected (mirrors the old quotes.html "Open checklist").
  const openChecklistForCase = (record) => {
    const quoteNum = record.quoteNum || record.normalizedQuoteNum || record.deal?.quoteNum || '';
    window.history.replaceState(null, '', `/?quoteNum=${encodeURIComponent(quoteNum)}&intakeId=${encodeURIComponent(record.id)}&tab=checklist`);
    onTabChange('checklist');
  };

  // Old quotes.html rendered installed/total ("X/Y") only for partial/complete rows.
  const siteCountText = (q) => {
    const installed = Number(q.installedSiteCount || 0);
    const total = Number(q.totalSiteCount || q.siteCount || 0);
    if ((q.status === 'partially_installed' || q.status === 'install_complete') && total) {
      return `${installed}/${total}`;
    }
    return String(q.totalSiteCount || q.siteCount || 0);
  };

  const openIntakeCase = (q, revision) => {
    window.history.replaceState(null, '', revision ? `/?id=${q.id}&revision=quote` : `/?id=${q.id}`);
    onTabChange('intake');
  };

  // Role-aware queue action: { label, onClick } renders a button, { info } renders
  // plain text. AEs never get buttons — they act through the emailed sign-off link.
  const queueAction = (q) => {
    if (role === 'ae') {
      if (q.status === 'inaccurate_data_claim') return { info: 'Pending contracts review' };
      if (q.status === 'needs_im_revision') return { info: 'Claim approved — IM revising' };
      if (q.status === 'new_quote_needed') return { info: 'Awaiting updated quote' };
      return { info: 'Sign off via the emailed link' };
    }
    if (q.status === 'inaccurate_data_claim') {
      if (canResolveClaim(role) || canViewClaimReadOnly(role)) {
        return { label: 'Review claim', onClick: () => initiateInaccuracyReview(q.id) };
      }
      return { info: 'Pending Contracts review' };
    }
    if (q.status === 'new_quote_needed') {
      // Switches workspace back to intake terminal mapping parameter updates context
      if (canOpenIntakeCase(role)) return { label: 'Regenerate / swap quote mapping', onClick: () => openIntakeCase(q, true) };
      // Contracts re-open their resolved claim read-only instead of regenerating.
      if (canResolveClaim(role)) return { label: 'View claim', onClick: () => initiateInaccuracyReview(q.id) };
      return { info: 'Awaiting updated quote' };
    }
    if (q.status === 'needs_im_revision') {
      if (canOpenIntakeCase(role)) return { label: 'Open Case', onClick: () => openIntakeCase(q) };
      if (canResolveClaim(role)) return { label: 'View claim', onClick: () => initiateInaccuracyReview(q.id) };
      return { info: 'IM revising' };
    }
    if (q.status === 'install_complete') {
      // Fully installed cases open the checklist directly (old quotes.html behavior).
      if (canOpenChecklistTab(role)) return { label: 'Open Case', onClick: () => openChecklistForCase(q) };
      return { info: 'Install complete' };
    }
    if (q.status === 'ready_to_install' || q.status === 'partially_installed') {
      if (canValidateInstall(role)) return { label: 'Open Case', onClick: () => initiateSiteValidation(q.id) };
      return { info: 'Awaiting site validation' };
    }
    if (canOpenIntakeCase(role)) return { label: 'Open Case', onClick: () => openIntakeCase(q) };
    return { info: '—' };
  };

  return (
    <div className="queue-wrapper">
      {/* GLOBAL LOCAL TERMINAL SYSTEM CHROME NAVBAR */}
      <div id="chrome">
        <div className="chrome-logo"><span>SF</span> Sales Cloud</div>
        <div className="chrome-nav">
          {CHROME_TABS.filter(t => isTabAllowed(role, t.id)).map(t => (
            <button key={t.id} type="button" className={`chrome-nav-item ${t.id === 'queue' ? 'active' : ''}`} onClick={() => onTabChange(t.id)}>{t.label}</button>
          ))}
        </div>
        <div className="chrome-crumb"><span>Dashboard</span><span className="chrome-sep">/</span><b>Queue</b></div>
        <div className="chrome-actions"><button type="button" className="sf-btn" onClick={loadQueueData}>Refresh</button></div>
      </div>

      <main className="shell">
        <div className="hero">
          <div>
            <h1>Queue</h1>
            <p>Track cases from intake through AE approval, installed-site validation, and service checklist handoff.</p>
          </div>
        </div>

        {/* DUPLICATE DATA EXCEPTIONS LOG ALERTER CONTAINER */}
        {duplicateGroups.length > 0 && !['ae', 'contracts', 'restricted'].includes(role) && (
          <div className="alert amber">
            <strong>Duplicate Quote Index Conflicts Traced:</strong>
            <div className="dup-list">
              {duplicateGroups.map((grp, idx) => (
                <div key={idx}>
                  <strong>{grp[0]?.quoteNum || "Unknown Profile"}</strong>:{" "}
                  {grp.map(item => (
                    <button key={item.id} type="button" className="btn text-link" style={{ background: 'none', border: 'none', padding: 0, color: 'var(--blue)', textDecoration: 'underline', cursor: 'pointer', marginRight: '8px' }} onClick={() => { window.history.replaceState(null, '', `/?id=${item.id}`); onTabChange('intake'); }}>
                      {item.statusLabel || item.status} ({item.id})
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* INACCURATE DATA RESOLUTION FRAMEWORK VIEW (legacy quotes.html wording) */}
        {inaccuracyContext && (
          <section className="card validation-card">
            <div className="card-hd">
              <div>
                <h2>{inaccuracyContext.intake?.status === 'new_quote_needed' ? "New quote needed" : "Inaccurate data claim"}</h2>
                {inaccuracyContext.intake && (
                  <p>
                    {inaccuracyContext.intake.caseNumber || '—'} / {inaccuracyContext.intake.deal?.quoteNum || '—'} / {inaccuracyContext.intake.deal?.oppName || '—'}
                  </p>
                )}
              </div>
              <button type="button" className="btn" onClick={() => setInaccuracyContext(null)}>Close</button>
            </div>
            <div className="card-bd">
              <div className="alert red">{inaccuracyContext.message}</div>
              {inaccuracyContext.intake && (
                <>
                  <div className="field" style={{ marginTop: '12px' }}>
                    <label>AE inaccurate-data comment</label>
                    <textarea value={inaccuracyContext.intake.inaccuracy?.aeComment || "No comment captured."} readOnly />
                  </div>
                  {inaccuracyContext.intake.inaccuracy?.imReason && (
                    <div className="field" style={{ marginTop: '12px' }}>
                      <label>IM reason</label>
                      <textarea value={inaccuracyContext.intake.inaccuracy.imReason} readOnly />
                    </div>
                  )}
                  {inaccuracyContext.intake.status !== 'new_quote_needed' && inaccuracyContext.intake.status !== 'needs_im_revision' && (
                    canResolveClaim(role) ? (
                      <>
                        <div className="field" style={{ marginTop: '12px' }}>
                          <label>IM reason / notes</label>
                          <textarea value={inaccuracyContext.reason} onChange={e => setInaccuracyContext(prev => ({ ...prev, reason: e.target.value }))} placeholder="Required if a new quote is needed. Optional for approval notes." />
                        </div>
                        <div className="toolbar" style={{ marginTop: '14px' }}>
                          <button type="button" className="btn red" onClick={() => commitInaccuracyResolution('new_quote_needed')} disabled={inaccuracyContext.saving}>Reject - New Quote Needed</button>
                          <button type="button" className="btn green" onClick={() => commitInaccuracyResolution('approve')} disabled={inaccuracyContext.saving}>Approve request - allow IM edits</button>
                        </div>
                      </>
                    ) : (
                      <div className="alert amber" style={{ marginTop: '12px' }}>Pending Contracts review.</div>
                    )
                  )}
                </>
              )}
            </div>
          </section>
        )}

        {/* INSTALLED ELEMENT VALIDATION SELECTION CONTROLLER PANELS */}
        {validationContext && (
          <section className="card validation-card">
            <div className="card-hd">
              <div>
                <h2>Validate Site Installation Scope Boundaries</h2>
                {validationContext.intake && <p>Case Tracking Array Reference Token: {validationContext.intake.caseNumber}</p>}
              </div>
              <button type="button" className="btn" onClick={() => setValidationContext(null)}>Close Worksheet</button>
            </div>
            <div className="card-bd">
              <div className="alert blue">{validationContext.message}</div>
              {validationContext.intake && (
                <>
                  <div className="validation-fields">
                    <div className="field">
                      <label>Deploying Field Engineer Identity</label>
                      <input type="text" value={validationContext.installedBy} onChange={e => setValidationContext(prev => ({ ...prev, installedBy: e.target.value }))} placeholder="Full field operator name string..." />
                    </div>
                    <div className="field">
                      <label>Deployment Logistics Validation Exception Notes</label>
                      <textarea value={validationContext.notes} onChange={e => setValidationContext(prev => ({ ...prev, notes: e.target.value }))} placeholder="Log site exceptions, configuration blockades, or target node environment metrics anomalies data..." />
                    </div>
                  </div>
                  <div className="validation-sites">
                    {(validationContext.intake.sites || []).map(site => {
                      const selectedSet = new Set(validationContext.installedSiteIds);
                      const isChecked = selectedSet.has(site.id);
                      return (
                        <label key={site.id} className="validation-site">
                          <input type="checkbox" checked={isChecked} onChange={e => handleValidationCheckboxChange(site.id, e.target.checked)} />
                          <span>
                            <span className="validation-title">{site.name}</span>
                            <span className="validation-meta"><span>{site.type}</span><span>{site.region}</span></span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="toolbar" style={{ marginTop: '14px' }}>
                    <button type="button" className="btn primary" onClick={commitValidationPayload} disabled={validationContext.saving || !validationContext.installedSiteIds.length}>
                      {validationContext.saving ? "Syncing Workspace Grid..." : "Commit Physical Installation Parameters"}
                    </button>
                    {canOpenChecklistTab(role) && (validationContext.intake.status === 'partially_installed' || validationContext.intake.status === 'install_complete') && (
                      <button type="button" className="btn green" onClick={() => openChecklistForCase(validationContext.intake)}>
                        Open checklist →
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </section>
        )}

        {/* CORE DATABASE GRID INSTANCE LAYOUT CONTAINER SHEET */}
        <section className="card">
          <div className="card-hd">
            <div><h2>Case Queue</h2><p>{statusMessage}</p></div>
            <div className="toolbar">
              <label className="toggle">
                <input type="checkbox" checked={includeInstallComplete} onChange={e => setIncludeInstallComplete(e.target.checked)} />
                Show install complete
              </label>
              {canCreateIntake(role) && (
                <button type="button" className="btn primary" onClick={() => { window.history.replaceState(null, '', '/'); onTabChange('intake'); }}>New Intake</button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="empty"><strong>Refreshing...</strong><span>Please hold.</span></div>
          ) : !quotes.length ? (
            <div className="empty"><strong>No cases to show.</strong>
                <span>{includeInstallComplete ? "Create a new intake to start tracking cases." : "Turn on Show install complete or create a new intake."}</span></div>
          ) : (
            <>
            <div className="alert blue">{quotes.length} case {quotes.length === 1 ? 'record' : 'records'} found</div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Quote</th>
                    <th>Status</th>
                    <th>Opportunity</th>
                    <th>AE</th>
                    <th>Sites</th>
                    <th>Last Activity</th>
                    <th>Case</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.map(q => (
                    <tr key={q.id}>
                      <td><div className="main-cell mono">{q.quoteNum ||q.normalizedQuoteNum}</div><div className="sub-cell">{q.id}</div></td>
                      <td>
                        <span className={`status ${q.status}`}>
                          {
                            roleStatusLabel(role, q) ||
                            {
                              draft: 'In Progress',
                              approved: 'Completed',
                              ready_to_install: 'Ready to Install'
                            }[q.status?.toLowerCase()] || q.statusLabel || q.status
                          }
                        </span>
                      </td>
                      <td><div className="main-cell">{ q.opportunityName||  "Untitled Contract Objective"}</div><div className="sub-cell">{q.parentAccount || "Isolated Destination Entity"}</div></td>
                      <td>{q.accountExecutive || "-"}</td>
                      <td>{siteCountText(q)}</td>
                      <td>{formatEpochTimestamp(q.signedAt || q.updatedAt || q.createdAt)}</td>
                      <td className="mono">{q.caseNumber || "-"}</td>
                      <td>
                        <div className="action-cell">
                          {(() => {
                            const action = queueAction(q);
                            return action.info ? (
                              <span style={{ fontSize: '12px', color: 'var(--g500)' }}>{action.info}</span>
                            ) : (
                              <button type="button" className="btn primary" onClick={action.onClick}>{action.label}</button>
                            );
                          })()}
                          {canDownloadChecklists(role) && q.checklistBundles && q.checklistBundles.length > 0 && (
                            <details className="download-menu">
                              <summary className="btn">Download Checklists</summary>
                              <div className="download-menu-panel">
                                {q.checklistBundles.map((b, bIdx) => (
                                  <button key={bIdx} type="button" onClick={() => downloadFile(api.getChecklistPdfBundleUrl(q.quoteNum, b.siteId, q.id), `${b.siteName || b.siteId || 'site'}-checklist-bundle.zip`)}>
                                    <strong>{b.siteName || b.siteId || "Completed Site Trace Instance"}</strong>
                                    {b.completedAt && <span>Verified: {formatEpochTimestamp(b.completedAt)}</span>}
                                  </button>
                                ))}
                              </div>
                            </details>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </section>
      </main>
    </div>
  );
}